import type { ListBuilderConfiguration } from '../types';

/**
 * Item Lists list configuration (PD-15839) — the `/items/lists` page. Rows
 * come from GET /item-lists and are keyed by `_id`; the actions column routes
 * to the page's registered callbacks (mirrors client_list.config.ts).
 */
export const ITEM_LISTS_CONFIG: ListBuilderConfiguration = {
	lists: [
		{
			id: 'default-item-lists-all',
			cacheTTL: 600000,
			name: 'All Item Lists',
			type: 'default',
			active: true,
			columns: [
				{
					id: 'list-name',
					fields: [
						{
							id: 'name-field',
							link: {
								type: 'object',
								enabled: true,
								objectTable: 'item_list',
								idField: '_id',
							},
							dataConfig: {
								type: 'field',
								field: { fieldName: 'name', objectTable: 'item_list' },
							},
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
						{
							id: 'line-break-1',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: { size: 'md', colorRule: { type: 'default' } },
							displayStyle: 'line_break',
						},
						{
							id: 'description-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'description', objectTable: 'item_list' },
							},
							styleConfig: {
								size: 'xs',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h1', text: 'List Name' }],
					minWidth: 200,
					sortable: true,
					alignment: 'left',
					sortFields: [
						{ direction: 'ASC', fieldName: 'name', objectTable: 'item_list' },
					],
					widthConfig: { mode: 'flexible', preset: 'large' },
					verticalAlignment: 'top',
				},
				{
					id: 'item-count',
					fields: [
						{
							id: 'item-count-field',
							dataConfig: {
								type: 'field',
								field: { fieldName: 'itemCount', objectTable: 'item_list' },
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'default' },
							},
							displayStyle: 'text',
						},
					],
					headers: [{ id: 'h2', text: '# Items' }],
					sortable: false,
					alignment: 'center',
					widthConfig: { mode: 'fixed', width: 100 },
					verticalAlignment: 'top',
				},
				{
					id: 'created-date',
					fields: [
						{
							id: 'created-date-field',
							dataConfig: {
								type: 'field',
								field: {
									fieldName: '_created_date',
									objectTable: 'item_list',
								},
							},
							styleConfig: {
								size: 'sm',
								colorRule: { type: 'theme', themeColor: 'textSecondary' },
							},
							displayStyle: 'date',
						},
					],
					headers: [{ id: 'h3', text: 'Created' }],
					sortable: true,
					alignment: 'left',
					sortFields: [
						{
							direction: 'DESC',
							fieldName: '_created_date',
							objectTable: 'item_list',
						},
					],
					widthConfig: { mode: 'fixed', width: 120 },
					verticalAlignment: 'top',
				},
				{
					id: 'actions',
					fields: [
						{
							id: 'item-list-actions',
							dataConfig: { type: 'custom_text', customText: '' },
							styleConfig: {
								size: 'md',
								colorRule: { type: 'default' },
							},
							displayStyle: 'actions_dropdown',
							dropdownActions: [
								// Row-level gating mirrors the server rules (ItemListsService):
								// adding members and editing need the list's edit right
								// (manager, or shared with allow-edit); deleting is manager-only.
								{
									id: 'add-item-action',
									label: 'Add Item',
									iconName: 'plus',
									callbackName: 'addItem',
									variant: 'secondary',
									visibility: 'visible',
									conditionalVisibility: {
										field: 'canEdit',
										condition: 'equals',
										value: true,
									},
								},
								{
									id: 'add-as-tab-action',
									label: 'Add as Tab',
									iconName: 'table-columns',
									callbackName: 'addAsTab',
									variant: 'secondary',
									visibility: 'visible',
								},
								{
									id: 'edit-action',
									label: 'Edit',
									iconName: 'pen',
									callbackName: 'edit',
									variant: 'primary',
									visibility: 'visible',
									requiredPermission: 'items:lists:manage',
									conditionalVisibility: {
										field: 'canEdit',
										condition: 'equals',
										value: true,
									},
								},
								{
									id: 'delete-action',
									label: 'Delete',
									iconName: 'trash',
									callbackName: 'delete',
									variant: 'danger',
									visibility: 'visible',
									requiredPermission: 'items:lists:manage',
									conditionalVisibility: {
										field: 'isManager',
										condition: 'equals',
										value: true,
									},
								},
							],
						},
					],
					headers: [{ id: 'h5', text: 'Actions' }],
					sortable: false,
					alignment: 'right',
					widthConfig: { mode: 'fixed', width: 180 },
					verticalAlignment: 'top',
				},
			],
			description: 'All item lists',
			defaultSortId: 'name-asc',
			requiredFields: [
				'item_list._id',
				'item_list.name',
				'item_list.description',
				'item_list.itemCount',
				'item_list._created_date',
			],
			sortOrder: 0,
		},
	],
	paging: {
		defaultPageSize: 25,
		pageSizeOptions: [25, 50, 100],
	},
	filters: {
		activeFilters: [],
		defaultFilters: {},
	},
	objects: [
		{
			baseUrl: '/items/lists/:id',
			objectLabel: 'Item List',
			objectTable: 'item_list',
			selectedFields: [
				{ fieldName: '_id', fieldLabel: 'ID' },
				{ fieldName: 'name', fieldLabel: 'Name' },
				{ fieldName: 'description', fieldLabel: 'Description' },
				{ fieldName: 'itemCount', fieldLabel: 'Item Count' },
				{ fieldName: '_created_date', fieldLabel: 'Created Date' },
			],
		},
	],
	tableSorts: [
		{
			id: 'name-asc',
			name: 'Name (A-Z)',
			fields: [
				{ direction: 'ASC', fieldName: 'name', objectTable: 'item_list' },
			],
			description: 'Sort lists by name',
		},
		{
			id: 'name-desc',
			name: 'Name (Z-A)',
			fields: [
				{ direction: 'DESC', fieldName: 'name', objectTable: 'item_list' },
			],
			description: 'Sort lists by name descending',
		},
		{
			id: 'created-desc',
			name: 'Newest First',
			fields: [
				{
					direction: 'DESC',
					fieldName: '_created_date',
					objectTable: 'item_list',
				},
			],
			description: 'Sort by creation date',
		},
	],
};
